/**
 * Validates if a Tax ID (SSN or EIN) is complete and valid
 * @param taxId - The tax ID to validate
 * @returns Object containing validation result and details
 */
export function validateTaxId(taxId: string | null | undefined): {
  isValid: boolean;
  isMissing: boolean;
  isIncomplete: boolean;
  isMasked?: boolean;
  message?: string;
} {
  // Check if Tax ID is missing
  if (!taxId || taxId.trim().length === 0) {
    return {
      isValid: false,
      isMissing: true,
      isIncomplete: false,
      message: 'Tax ID is not provided. Please ensure the client has a valid Tax ID in CCH.',
    };
  }

  // Remove all non-digit characters for validation
  const cleanedTaxId = taxId.replace(/\D/g, '');

  // If exactly 4 digits, it's a masked value from the backend (last 4 digits)
  if (cleanedTaxId.length === 4) {
    return {
      isValid: true,
      isMissing: false,
      isIncomplete: false,
      isMasked: true,
    };
  }

  // Check if the Tax ID is incomplete (less than 4 or between 5-8 digits)
  if (cleanedTaxId.length < 4 || (cleanedTaxId.length > 4 && cleanedTaxId.length < 9)) {
    return {
      isValid: false,
      isMissing: false,
      isIncomplete: true,
      message: `Tax ID appears incomplete (only ${cleanedTaxId.length} digits). A valid Tax ID should be 4 digits (masked) or 9 digits (full).`,
    };
  }

  // Check if all digits are the same (invalid pattern) - only for full 9-digit IDs
  if (cleanedTaxId.length === 9 && /^(\d)\1{8}$/.test(cleanedTaxId)) {
    return {
      isValid: false,
      isMissing: false,
      isIncomplete: false,
      message: 'Tax ID appears invalid (all digits are the same).',
    };
  }

  // Check for sequential patterns (e.g., 123456789) - only for full 9-digit IDs
  if (cleanedTaxId === '123456789' || cleanedTaxId === '987654321') {
    return {
      isValid: false,
      isMissing: false,
      isIncomplete: false,
      message: 'Tax ID appears to be a test or placeholder value.',
    };
  }

  // If we have exactly 9 digits and passed other checks, it's valid
  if (cleanedTaxId.length === 9) {
    return {
      isValid: true,
      isMissing: false,
      isIncomplete: false,
    };
  }

  // If we have more than 9 digits, it might be malformed
  if (cleanedTaxId.length > 9) {
    return {
      isValid: false,
      isMissing: false,
      isIncomplete: false,
      message: `Tax ID appears malformed (${cleanedTaxId.length} digits). A valid Tax ID should be 4 digits (masked) or 9 digits (full).`,
    };
  }

  // Fallback - should not reach here
  return {
    isValid: false,
    isMissing: false,
    isIncomplete: false,
    message: 'Tax ID validation failed due to unexpected format.',
  };
}

/**
 * Formats a Tax ID for display (masks sensitive digits)
 * @param taxId - The tax ID to format
 * @param showFull - Whether to show the full Tax ID (default: false)
 * @returns Formatted Tax ID string
 */
export function formatTaxId(taxId: string | null | undefined, showFull = false): string {
  if (!taxId) return 'Not provided';

  const cleanedTaxId = taxId.replace(/\D/g, '');

  if (cleanedTaxId.length === 0) return 'Not provided';

  // If exactly 4 digits, it's already masked by the backend - format as ***-**-XXXX
  if (cleanedTaxId.length === 4) {
    return `***-**-${cleanedTaxId}`;
  }

  // If showing full and we have 9 digits, format as XXX-XX-XXXX
  if (showFull && cleanedTaxId.length === 9) {
    return `${cleanedTaxId.slice(0, 3)}-${cleanedTaxId.slice(3, 5)}-${cleanedTaxId.slice(5, 9)}`;
  }

  // If we have 9 digits and not showing full, mask to show only last 4
  if (cleanedTaxId.length === 9 && !showFull) {
    return `***-**-${cleanedTaxId.slice(-4)}`;
  }

  // For incomplete IDs (less than 4 or between 5-8 digits), return as-is
  return taxId;
}

/**
 * Masks a Tax ID for secure display on the client selection screen
 * Ensures only the last 4 digits are visible, regardless of what the backend sends
 *
 * @param taxId - The tax ID to mask (can be already partially masked or full)
 * @param type - The type of tax ID ('SSN' | 'EIN')
 * @returns Masked Tax ID string showing only last 4 digits
 *
 * @example
 * maskTaxIdForDisplay('***-**-414456322', 'SSN') // Returns '***-**-6322'
 * maskTaxIdForDisplay('**-*****85', 'EIN') // Returns '**-****6785' (if more digits available)
 * maskTaxIdForDisplay('123456789', 'SSN') // Returns '***-**-6789'
 * maskTaxIdForDisplay('123456789', 'EIN') // Returns '**-****6789'
 */
export function maskTaxIdForDisplay(taxId: string | null | undefined, type: 'SSN' | 'EIN'): string {
  if (!taxId) return 'Not provided';

  // Extract all digits from the tax ID (removes dashes, asterisks, etc.)
  const digits = taxId.replace(/\D/g, '');

  if (digits.length === 0) return 'Not provided';

  // Get the last 4 digits
  const lastFour = digits.slice(-4);

  // Format based on type
  if (type === 'SSN') {
    // SSN format: ***-**-XXXX
    return `***-**-${lastFour}`;
  } else {
    // EIN format: **-****XXXX
    return `**-****${lastFour}`;
  }
}
